import React, { useState, useRef } from 'react';
import API from '../utils/api';
import './HandoffVerification.css';

const HandoffVerification = ({ bookingId, userRole, onVerified }) => {
  const [photoUrl, setPhotoUrl] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [conditionNotes, setConditionNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [locationError, setLocationError] = useState('');
  const [status, setStatus] = useState('pending'); // pending, uploading, verified
  const fileInputRef = useRef(null);

  const isRenter = userRole === 'buyer';
  const endpoint = isRenter
    ? `/handoff/renter-confirm`
    : `/handoff/seller-confirm`;

  const getLocation = () => {
    setLocationError('');
    if (!navigator.geolocation) {
      setLocationError('Geolocation not supported by your browser');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLatitude(position.coords.latitude);
        setLongitude(position.coords.longitude);
        setLocationError('');
      },
      (err) => {
        let msg = 'Unable to get location. ';
        if (err.code === 1) msg += 'Please enable location access in your browser settings.';
        else if (err.code === 2) msg += 'Location service unavailable.';
        else if (err.code === 3) msg += 'Location request timed out.';
        setLocationError(msg);
      },
      { timeout: 10000, enableHighAccuracy: true }
    );
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setPhotoPreview(reader.result);
      setPhotoUrl(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!photoUrl) {
      setError('Please take or upload a photo');
      return;
    }
    if (latitude === null || longitude === null) {
      setError('Please enable location access');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await API.post(`/bookings/${bookingId}${endpoint}`, {
        photo_url: photoUrl,
        photo_timestamp: new Date().toISOString(),
        latitude,
        longitude,
        condition_notes: conditionNotes,
      });

      setStatus('verified');
      if (onVerified) onVerified(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit handoff');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="handoff-verification">
      <div className="handoff-header">
        <h3>📸 Confirm Item Handoff</h3>
        <p>
          {isRenter
            ? 'Take a photo proving you received the item'
            : 'Take a photo proving you handed off the item'}
        </p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {locationError && <div className="alert alert-warning">⚠️ {locationError}</div>}

      <div className="handoff-content">
        {/* Photo Capture */}
        <div className="photo-section">
          <div className="photo-preview-box">
            {photoPreview ? (
              <>
                <img src={photoPreview} alt="Handoff" />
                <div className="photo-status">✓ Photo captured</div>
              </>
            ) : (
              <div className="photo-placeholder">
                <div style={{ fontSize: 48, marginBottom: 12 }}>📷</div>
                <p>No photo yet</p>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <button
              className="btn btn-cherry"
              onClick={() => fileInputRef.current?.click()}
              disabled={loading}
            >
              📤 Choose Photo
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handlePhotoChange}
              style={{ display: 'none' }}
            />
          </div>
        </div>

        {/* Location */}
        <div className="location-section">
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontWeight: 600, fontSize: 14, display: 'block', marginBottom: 8 }}>
              📍 Meetup Location
            </label>
            {latitude && longitude ? (
              <div
                style={{
                  padding: 12,
                  background: '#E8F5E9',
                  borderRadius: 8,
                  fontSize: 13,
                  color: '#2E7D32',
                  fontWeight: 600,
                }}
              >
                ✓ Location captured: {latitude.toFixed(4)}, {longitude.toFixed(4)}
              </div>
            ) : (
              <button className="btn btn-primary" onClick={getLocation} style={{ width: '100%' }}>
                📍 Enable Location
              </button>
            )}
          </div>
        </div>

        {/* Condition Notes */}
        <div className="notes-section">
          <label style={{ fontWeight: 600, fontSize: 14, display: 'block', marginBottom: 8 }}>
            Notes (optional)
          </label>
          <textarea
            className="form-textarea"
            placeholder="e.g., Item in excellent condition, no issues"
            value={conditionNotes}
            onChange={(e) => setConditionNotes(e.target.value)}
            style={{ minHeight: 80, fontSize: 13 }}
          />
        </div>

        {/* Submit */}
        <button
          className="btn btn-cherry btn-lg"
          onClick={handleSubmit}
          disabled={loading || !photoUrl || latitude === null}
          style={{ width: '100%', marginTop: 20 }}
        >
          {loading ? (
            <>
              <span className="spinner" /> Submitting...
            </>
          ) : (
            '✓ Confirm Handoff'
          )}
        </button>
      </div>

      {status === 'verified' && (
        <div className="alert alert-success" style={{ marginTop: 16 }}>
          ✓ Handoff verified! Waiting for the other party to confirm.
        </div>
      )}
    </div>
  );
};

export default HandoffVerification;
